import type { RecipientTag } from 'components/common/recipient-input'

type IdNamespace = 'business' | 'contact'

// Contacts and businesses are independent auto-increment id spaces, so a
// business and a contact can legitimately share the same numeric id —
// comparing bare contactId values would wrongly treat that as a duplicate.
const namespaceOf = (type?: RecipientTag['type']): IdNamespace =>
  type === 'business' ? 'business' : 'contact'

/** Whether two recipient tags refer to the same underlying recipient, used
 * to prevent adding a duplicate to a picked/recipient list. */
export const isSameRecipient = (
  a: Pick<RecipientTag, 'contactId' | 'type' | 'email'>,
  b: Pick<RecipientTag, 'contactId' | 'type' | 'email'>,
): boolean => {
  if (a.contactId != null && b.contactId != null) {
    return (
      a.contactId === b.contactId && namespaceOf(a.type) === namespaceOf(b.type)
    )
  }

  const aEmail = a.email?.toLowerCase()
  const bEmail = b.email?.toLowerCase()
  return Boolean(aEmail && bEmail && aEmail === bEmail)
}

/** Maps recipient tags to the shape `trackShare` expects: matched contacts
 * and businesses go by id, everything else goes by email. `business_ids` is
 * always null — the API treats a null/empty value as "no restriction" (i.e.
 * every business the recipient belongs to), which is the only behavior this
 * UI has ever offered; there's no picker for narrowing it further. */
export const buildShareRecipients = (recipients: RecipientTag[]) =>
  recipients
    .filter(r => r.isValid || r.type === 'user' || r.type === 'business')
    .map(r => {
      if (r.type === 'user' || r.type === 'business') {
        return { id: r.contactId, type: r.type, business_ids: null }
      }
      return { type: 'email' as const, email: r.email, name: r.name }
    })
